/**
 * Where a client-side navigation lands the page -- R13-Z4 (owner review 5, P4: "when it takes me to the
 * download page I end up toward the bottom … when a user navigates to a new page they land on the top,
 * unless navigating back, where the browser retains the position").
 *
 * Why the browser does not already do this here: `<body>` is this site's scroller (globals.scss: `html,
 * body { height: 100% }` + `body { overflow-y: scroll }`; measured on QA: `scrollingElement` is <html>
 * but only <body> scrolls). Next's pages router calls `window.scrollTo(0, 0)` after every push -- a no-op
 * on a body scroller -- and restores nothing on popstate for a same-document navigation. So the position
 * a visitor had on the previous page simply carried over, and round 7's "scroll to the newest card" on
 * /downloads then ran on EVERY arrival, push and Back alike.
 *
 * The rule, bound once from `pages/_app.tsx`:
 *   - push to a different pathname → the new page lands at the TOP;
 *   - Back/forward (popstate) to a different pathname → the position the visitor left that entry at is
 *     put back (remembered per history key at `routeChangeStart`, i.e. before the DOM is swapped);
 *   - query-only changes (`?tab=studio`, `?tab=picker`, shallow writes) → untouched: opening a tab is not
 *     a new page (S2/P6: no programmatic scroll on tab open).
 * A page whose real content renders after the route settled (/downloads: the 1000 px placeholder, then
 * the cards) calls `settlePageLanding()` once to re-apply the same landing -- a restore clamped by the
 * placeholder's height, or a top that content growth might have nudged.
 */
import Router from 'next/router';

export type PageArrival = 'initial' | 'push' | 'pop' | 'same';
export type Landing = { kind: 'top' } | { kind: 'restore'; y: number } | { kind: 'none' };

/** The page's scroll position, read and written on whichever element scrolls. Injectable for tests. */
export interface LandingEnv {
    read(): number;
    write(y: number): void;
    /** Next's `history.state.key` for the current entry, `null` when there is none yet. */
    historyKey(): string | null;
}

export const domLandingEnv: LandingEnv = {
    read: () => {
        if (typeof document === 'undefined') return 0;
        return Math.max(document.body?.scrollTop || 0, document.documentElement?.scrollTop || 0, (typeof window !== 'undefined' && window.scrollY) || 0);
    },
    write: (y: number) => {
        if (typeof document === 'undefined') return;
        try {
            if (document.body) document.body.scrollTop = y;
            if (document.documentElement) document.documentElement.scrollTop = y;
            window.scrollTo(0, y);
        } catch { /* no layout (SSR, jsdom) */ }
    },
    historyKey: () => {
        try {
            const key = typeof history !== 'undefined' && history.state && (history.state as any).key;
            return typeof key === 'string' ? key : null;
        } catch { return null; }
    },
};

export function pathnameOf(url: string): string {
    const q = url.indexOf('?'), h = url.indexOf('#');
    const end = Math.min(q === -1 ? url.length : q, h === -1 ? url.length : h);
    return url.slice(0, end) || '/';
}

export class PageLanding {
    arrival: PageArrival = 'initial';
    private popPending = false;
    private currentKey: string | null = null;
    private currentPath: string | null = null;
    private readonly positions = new Map<string, number>();
    private landing: Landing = { kind: 'none' };

    constructor(private readonly env: LandingEnv) {}

    /** The entry the page loaded on. */
    begin(pathname: string, key: string | null): void {
        this.currentPath = pathnameOf(pathname);
        this.currentKey = key;
    }

    /** `Router.beforePopState`: the next route change is Back/forward. */
    markPop(): void { this.popPending = true; }

    /** `routeChangeError`: the change did not happen; forget the flag. */
    routeError(): void { this.popPending = false; }

    /** `routeChangeStart`: the DOM is still the departing page's -- remember where it was. */
    routeStart(): void {
        if (this.currentKey !== null) this.positions.set(this.currentKey, this.env.read());
    }

    /** `routeChangeComplete`: decide and apply the landing. */
    routeComplete(url: string, shallow = false): Landing {
        const pop = this.popPending;
        this.popPending = false;
        const path = pathnameOf(url);
        const changed = this.currentPath !== null && path !== this.currentPath;
        this.currentKey = this.env.historyKey();
        this.currentPath = path;
        if (pop) {
            this.arrival = 'pop';
            const y = changed && this.currentKey !== null ? this.positions.get(this.currentKey) : undefined;
            this.landing = y === undefined ? { kind: 'none' } : { kind: 'restore', y };
        } else if (changed && !shallow) {
            this.arrival = 'push';
            this.landing = { kind: 'top' };
        } else {
            this.arrival = 'same';
            this.landing = { kind: 'none' };
        }
        this.apply();
        return this.landing;
    }

    /** Re-apply this arrival's landing once the page's real content is there. */
    settle(): Landing {
        this.apply();
        return this.landing;
    }

    private apply(): void {
        if (this.landing.kind === 'top') this.env.write(0);
        else if (this.landing.kind === 'restore') this.env.write(this.landing.y);
    }
}

const landing = new PageLanding(domLandingEnv);
let bound = false;

/** Bind once (pages/_app.tsx componentDidMount). Safe to call again; safe without a window. */
export function bindPageLanding(): void {
    if (bound || typeof window === 'undefined') return;
    const events = Router.events;
    if (!events) return;
    bound = true;
    landing.begin(window.location.pathname, domLandingEnv.historyKey());
    Router.beforePopState(() => { landing.markPop(); return true; });
    events.on('routeChangeStart', () => landing.routeStart());
    events.on('routeChangeError', () => landing.routeError());
    events.on('routeChangeComplete', (url: string, opts?: { shallow?: boolean }) => landing.routeComplete(String(url), !!(opts && opts.shallow)));
}

/** How the current page was reached. */
export function pageArrival(): PageArrival { return landing.arrival; }

/** For a page whose content renders after the route settled: apply the same landing again. */
export function settlePageLanding(): Landing { return landing.settle(); }
